import { FC, useMemo, useCallback } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';

import styles from './TripRightPanelMap.module.scss';
import { TripDetail, TripStop } from '../../../../../types/trip.types';
import { ExpandIcon, FilterIcon } from '../../../../../components/Icons';
import { TripStopCoordinate, useGetTripStopCoordinatesQuery } from '../../../../../store/services/tripsApi';
import TripRouteMap from '../TripRouteMap/TripRouteMap';
import { useIsMobile } from '../../../../../hooks/useWindowWidth';
import {
  assignPinRoles,
  getOrderedTripStops,
  spreadOverlappingCoordinates,
  tripStopToRoutePoint,
  TripMapRoutePoint,
} from '../../../../../utils/tripMapStops';

interface TripRightPanelMapProps {
  trip: TripDetail;
  onShowStopDetails: (activity: TripStop) => void;
  onShowFilters: () => void;
  onTogglePanel: () => void;
  isExpanded?: boolean;
  showExpandButton?: boolean;
}

const TripRightPanelMap: FC<TripRightPanelMapProps> = ({
  trip,
  onShowStopDetails,
  onShowFilters,
  onTogglePanel,
  isExpanded = false,
  showExpandButton = false,
}) => {
  const isMobile = useIsMobile();

  const coordinatesFromTrip = useMemo(() => {
    const stops = getOrderedTripStops(trip);
    const raw = stops.map(tripStopToRoutePoint).filter((p): p is TripMapRoutePoint => p != null);
    return assignPinRoles(spreadOverlappingCoordinates(raw));
  }, [trip]);

  const {
    data: tripStopCoordinates,
    isLoading,
    error,
  } = useGetTripStopCoordinatesQuery(trip.id.toString(), {
    skip: coordinatesFromTrip.length > 0,
  });

  const routeCoordinates = useMemo((): TripStopCoordinate[] => {
    if (coordinatesFromTrip.length > 0) {
      return coordinatesFromTrip;
    }
    const addr = tripStopCoordinates?.address;
    if (!addr?.length) {
      return [];
    }
    return assignPinRoles(spreadOverlappingCoordinates(addr.map(a => ({ ...a }))));
  }, [coordinatesFromTrip, tripStopCoordinates]);

  const stopById = useMemo(() => {
    const m = new Map<number, TripStop>();
    Object.values(trip.trip_stops_by_day ?? {}).forEach(day => {
      day.forEach(s => m.set(s.id, s));
    });
    return m;
  }, [trip]);

  const getMobileMapHeight = () => {
    if (!isMobile) return undefined;
    return isExpanded ? 400 : 250;
  };

  const handleMarkerClick = useCallback(
    (coordinate: TripStopCoordinate) => {
      if (coordinate.stopId == null) {
        return;
      }
      const activity = stopById.get(coordinate.stopId);
      if (activity) {
        onShowStopDetails(activity);
      }
    },
    [onShowStopDetails, stopById]
  );

  const getContainerClass = () => {
    let className = styles.tripRightPanelMap;

    if (isMobile) {
      if (isExpanded) {
        className += ` ${styles.expandedMobile}`;
      } else {
        className += ` ${styles.compactMobile}`;
      }
    }

    return className;
  };

  const hasCoordinates = routeCoordinates.length > 0;
  const showApiError = Boolean(error) && coordinatesFromTrip.length === 0;
  const showLoading = coordinatesFromTrip.length === 0 && isLoading && !error;

  return (
    <Box className={getContainerClass()}>
      <Box className={styles.mapSection}>
        {showApiError && (
          <Box className={styles.emptyState}>
            <Typography
              variant="h9Bold"
              sx={{ color: '#222B3A', mb: 1 }}
            >
              Unable to Load Map
            </Typography>
            <Typography
              variant="h11"
              sx={{ color: '#5E6A81', textAlign: 'center' }}
            >
              There was an error loading the map. Please try again later.
            </Typography>
          </Box>
        )}

        {!showApiError && showLoading && (
          <Box className={styles.emptyState}>
            <Typography
              variant="h10"
              sx={{ color: '#5E6A81', textAlign: 'center' }}
            >
              Loading route data...
            </Typography>
          </Box>
        )}

        {!showApiError && !showLoading && !hasCoordinates && (
          <Box className={styles.emptyState}>
            <Typography
              variant="h9Bold"
              sx={{ color: '#222B3A', mb: 1 }}
            >
              No Locations Yet
            </Typography>
            <Typography
              variant="h11"
              sx={{ color: '#5E6A81', textAlign: 'center' }}
            >
              Add stops to your trip to see them on the map
            </Typography>
          </Box>
        )}

        {!showApiError && !showLoading && hasCoordinates && (
          <TripRouteMap
            routeCoordinates={routeCoordinates}
            onMarkerClick={handleMarkerClick}
            mobileHeight={getMobileMapHeight()}
          />
        )}

        {!isMobile && (
          <>
            <IconButton
              onClick={onTogglePanel}
              className={styles.desktopExpandButton}
              size="small"
            >
              <ExpandIcon
                className={styles.expandIcon}
                size={32}
              />
            </IconButton>
            <Box
              onClick={onShowFilters}
              className={styles.desktopFilterButton}
            >
              <FilterIcon size={16} />
              <Typography variant="h11">Filters</Typography>
            </Box>
          </>
        )}

        {isMobile && showExpandButton && (
          <IconButton
            onClick={onTogglePanel}
            className={styles.mobileExpandButton}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              width: 44,
              height: 44,
              backgroundColor: '#ffffff',
              borderRadius: '12px',
              border: '1px solid #dadfe8',
              zIndex: 1000,
              color: '#222B3A',
              '&:hover': {
                backgroundColor: '#f5f5f5',
              },
              '&:active': {
                transform: 'scale(0.95)',
              },
            }}
          >
            {isExpanded ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </IconButton>
        )}
      </Box>
    </Box>
  );
};

export default TripRightPanelMap;
